import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { PageHeader, Panel, EmptyState, fmtDate } from "@/components/shared/DashKit";
import { StatusBadge } from "@/components/shared/Badges";
import { useContent } from "@/context/ContentContext";
import { toast } from "sonner";
import { Loader2, Copy, Plus, Trash2, KeyRound } from "lucide-react";

export default function ApiPage() {
  const { t } = useContent();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState(null);

  const load = () => api.get("/api-keys").then((r) => { setKeys(r.data); setLoading(false); });
  useEffect(() => { load(); }, []);

  const create = async () => {
    setCreating(true);
    try { const { data } = await api.post("/api-keys"); setNewKey(data.api_key); load(); toast.success(t("api.generated", "Clé API générée")); }
    finally { setCreating(false); }
  };
  const revoke = async (id) => { await api.delete(`/api-keys/${id}`); load(); toast.success(t("api.revoked", "Clé révoquée")); };

  return (
    <div>
      <PageHeader title={t("api.title", "API Revendeur")} subtitle={t("api.subtitle", "Générez une clé API pour intégrer nos services par programmation.")}
        action={<button onClick={create} disabled={creating} data-testid="generate-key-btn" className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-accent disabled:opacity-50">{creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {t("api.generate", "Générer une clé")}</button>} />

      {newKey && (
        <div className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="new-key-banner">
          <div className="text-sm font-medium text-emerald-800">{t("api.copy_now", "Copiez votre clé maintenant — elle ne sera plus affichée.")}</div>
          <div className="mt-2 flex items-center gap-2">
            <code className="flex-1 break-all rounded bg-white p-2 font-mono text-xs">{newKey}</code>
            <button onClick={() => { navigator.clipboard.writeText(newKey); toast.success(t("api.copied", "Copié")); }} className="rounded border border-emerald-200 bg-white p-2"><Copy className="h-4 w-4" /></button>
          </div>
        </div>
      )}

      <Panel title={t("api.my_keys", "Mes clés API")} className="mb-6">
        {loading ? <div className="py-8 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-accent" /></div>
          : keys.length === 0 ? <EmptyState title={t("api.empty_title", "Aucune clé API")} subtitle={t("api.empty_sub", "Générez votre première clé pour accéder à l'API revendeur.")} />
          : (
            <div className="space-y-2">
              {keys.map((k) => (
                <div key={k.id} className="flex items-center justify-between rounded-lg border border-border p-3" data-testid={`api-key-${k.id}`}>
                  <div className="flex items-center gap-3">
                    <KeyRound className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <div className="font-mono text-sm">{k.prefix}••••••••••••</div>
                      <div className="text-xs text-muted-foreground">{t("api.created_on", "Créée le")} {fmtDate(k.created_at)} · {t("api.last_used", "Dernière utilisation:")} {k.last_used ? fmtDate(k.last_used) : t("api.never", "jamais")}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <StatusBadge status={k.status === "active" ? "active" : "closed"} />
                    {k.status === "active" && <button onClick={() => revoke(k.id)} data-testid={`revoke-key-${k.id}`} className="text-red-500 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>}
                  </div>
                </div>
              ))}
            </div>
          )}
      </Panel>

      <Panel title={t("api.docs", "Documentation de l'API")}>
        <p className="text-sm text-muted-foreground">{t("api.auth_pre", "Authentifiez-vous avec l'en-tête")} <code className="rounded bg-secondary px-1 font-mono text-xs">X-API-Key: {t("api.key_placeholder", "votre_clé")}</code>. {t("api.base_url", "URL de base:")} <code className="rounded bg-secondary px-1 font-mono text-xs">{API}/v1</code></p>
        <div className="mt-4 space-y-3 font-mono text-xs">
          {[
            ["GET", "/v1/services", t("api.ep.services", "Liste des services disponibles")],
            ["POST", "/v1/order", t("api.ep.order", "Créer une commande { service_id, target, quantity }")],
            ["GET", "/v1/order/{id}", t("api.ep.order_status", "Statut d'une commande")],
            ["GET", "/v1/orders", t("api.ep.orders", "Historique des commandes")],
            ["GET", "/v1/balance", t("api.ep.balance", "Solde du portefeuille")],
          ].map(([m, path, d]) => (
            <div key={path} className="flex items-start gap-3 rounded-lg border border-border p-3">
              <span className={`rounded px-2 py-0.5 text-[10px] font-bold text-white ${m === "GET" ? "bg-emerald-500" : "bg-blue-500"}`}>{m}</span>
              <div><div>{path}</div><div className="mt-0.5 font-sans text-muted-foreground">{d}</div></div>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}
